/**
 * Parses Content-Disposition HTTP header to extract filename
 * Implements RFC 6266 and RFC 5987 standards
 *
 * Priority: filename*=UTF-8'' (modern) > filename= (legacy ASCII)
 *
 * @param header - Content-Disposition header value
 * @returns Extracted filename or null if not found/invalid
 *
 * @example
 * parseContentDisposition('attachment; filename="file.txt"')
 * // Returns: "file.txt"
 *
 * parseContentDisposition('attachment; filename*=UTF-8\'\'file%20name.txt')
 * // Returns: "file name.txt"
 */
export function parseContentDisposition(header: string): string | null {
  if (!header || typeof header !== "string") {
    return null;
  }

  try {
    // Match filename*=UTF-8''<percent-encoded> (RFC 5987)
    const filenameStarMatch = header.match(/filename\*=UTF-8''([^;]+)/i);
    if (filenameStarMatch) {
      try {
        return decodeURIComponent(filenameStarMatch[1].trim());
      } catch (e) {
        console.warn("Failed to decode UTF-8 filename:", e);
      }
    }

    // Match filename="<value>" or filename=<value> (RFC 6266)
    const filenameMatch = header.match(/filename=["']?([^;"']+)["']?/i);
    if (filenameMatch) {
      return filenameMatch[1].trim();
    }

    return null;
  } catch (error) {
    console.warn("Failed to parse Content-Disposition header:", error);
    return null;
  }
}

/**
 * Sanitizes filename to prevent security vulnerabilities
 * Removes path traversal attempts, directory separators, and control characters
 *
 * @param filename - Raw filename from server
 * @returns Sanitized filename safe for file system
 *
 * @example
 * sanitizeFilename("../../../etc/passwd")
 * // Returns: "etcpasswd"
 *
 * sanitizeFilename("file\x00name.txt")
 * // Returns: "filename.txt"
 */
export function sanitizeFilename(filename: string): string {
  if (!filename || typeof filename !== "string") {
    return "download";
  }

  return (
    filename
      // Remove path traversal attempts
      .replace(/\.\.\//g, "")
      .replace(/\.\.\\/g, "")
      // Remove directory separators
      .replace(/[/\\]/g, "")
      // Remove null bytes and control characters
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x1F\x7F]/g, "")
      // Trim whitespace
      .trim()
      // Limit length to 255 characters
      .substring(0, 255) || "download"
  );
}
